import { isAxiosError } from 'axios';
import type { Status } from '~api/som_search';

import { LocalizationKey } from '~src/i18n/use-translation';

export function getStatusColor(status?: Status) {
  if (!status) {
    return 'neutral';
  }

  if (informativeStatuses.includes(status)) {
    return 'informative';
  } else if (successStatuses.includes(status)) {
    return 'success';
  } else if (warningStatuses.includes(status)) {
    return 'warning';
  } else if (criticalStatuses.includes(status)) {
    return 'critical';
  }

  return 'neutral';
}

export function getStatusLabel(status?: Status): LocalizationKey {
  return `shipments.status.${status ?? 'UNKNOWN'}`;
}

export function shouldShowStatusReason(status?: Status, message?: string): boolean {
  return !!message && !!status && ['CANCEL_FAILED', 'INVALID_BOOKING'].includes(status);
}

export function isBookingFailedResponse(error: unknown) {
  return isAxiosError(error) && error.status === 400;
}

export function isCancelFailedResponse(error: unknown) {
  return isAxiosError(error) && error.status && [400, 501].includes(error.status);
}

const informativeStatuses: Status[] = [
  'ARRIVED_AT_PICKUP_POINT',
  'BOOKED',
  'BOOKING_RECEIVED_BY_CARRIER',
  'DELIVERY_IN_PROGRESS',
  'FORWARDED_TO_THIRD_PARTY',
  'IN_CUSTOMS',
  'IN_TRANSIT',
  'NOTIFICATION_SENT',
  'PICKED_UP',
  'PICKUP_BOOKED',
  'PICKUP_REGISTERED',
  'PICKUP_REQUESTED',
  'PICKUP_SCHEDULED',
  'INSTORE_PARCEL_RETURNED',
  'INSTORE_ARRIVED_AT_PICKUP_POINT',
];

const warningStatuses: Status[] = [
  'CANCELED',
  'CONTACT_CARRIER_SUPPORT',
  'CONTACT_MERCHANT_SUPPORT',
  'DELAYED',
  'PICKUP_DELAYED',
  'REDIRECTED',
  'RETURNED_TO_SENDER',
  'RETURNED',
  'INSTORE_PARCEL_REFUSED',
  'INSTORE_PARCEL_OVERDUE',
  'INSTORE_PARCEL_RELOCATED',
];

const successStatuses: Status[] = ['DELIVERED', 'INSTORE_PARCEL_PICKED_UP'];

const criticalStatuses: Status[] = [
  'INVALID_BOOKING',
  'DAMAGED',
  'ERROR',
  'FAILED',
  'LOST',
  'UNDELIVERABLE',
  'CANCEL_FAILED',
];

const neutralStatuses: Status[] = ['INFO', 'UNKNOWN'];

export const ShipmentStatuses: Status[] = [
  'CREATED',
  ...informativeStatuses,
  ...successStatuses,
  ...warningStatuses,
  ...criticalStatuses,
  ...neutralStatuses,
];
